import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Responsive, WidthProvider, type Layout } from 'react-grid-layout';
import type { Adapter } from '../types';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import './spaceGridAdapter.scss';

const ResponsiveGridLayout = WidthProvider(Responsive);
const BREAKPOINTS = { lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 };
const DEFAULT_COLS = { lg: 12, md: 10, sm: 8, xs: 4, xxs: 2 };
const DEFAULT_GRID_POSITION = { x: 0, y: 0, width: 4, height: 4 };

// react-grid-layout containerPadding/margin, ported from `MetaspaceGrid.tsx` so both branches
// solve the identical rowHeight equation for the identical grid geometry (SC2, SC13).
const CONTAINER_PADDING_Y = 10;
const CONTAINER_PADDING_BOTTOM = 10;
const MARGIN_Y = 10;
const MIN_ROW_HEIGHT = 30;
const FALLBACK_AVAILABLE_HEIGHT = 600;

/**
 * Solves `rowHeight * rows + (rows - 1) * marginY + containerPaddingY + containerPaddingBottom =
 * availableHeight` for `rowHeight`, ported verbatim from `MetaspaceGrid.tsx`'s `updateRowHeight`
 * so the grid fills its viewport region identically to the legacy renderer.
 */
export function computeViewportFillRowHeight(availableHeight: number, rows: number): number {
  const safeRows = Math.max(rows, 1);
  const heightForRows = availableHeight - CONTAINER_PADDING_Y - CONTAINER_PADDING_BOTTOM - (safeRows - 1) * MARGIN_Y;
  return Math.max(MIN_ROW_HEIGHT, Math.floor(heightForRows / safeRows));
}

/** `metaspaceToSpace` emits `children`; the catalog names the prop `sections` — accept either. */
function readChildIds(props: Record<string, unknown>): string[] {
  const raw = (props.children ?? props.sections) as unknown;
  return Array.isArray(raw) ? raw.filter((id): id is string => typeof id === 'string') : [];
}

/**
 * `SpaceGrid` catalog entry adapter — `surface.layout: 'uniform'` (§7.4, OQ1). Arranges child
 * Sections in a responsive `react-grid-layout` grid, extracted from the legacy `MetaspaceGrid`
 * organism switch so pixel placement of converted seeds stays parity-identical (SC2, SC13).
 */
const SpaceGridAdapter: Adapter = ({ componentId, props, ctx }) => {
  const ids = readChildIds(props);
  const columns = typeof props.columns === 'number' ? props.columns : DEFAULT_COLS.lg;
  const layoutContainerRef = useRef<HTMLDivElement>(null);
  const [computedRowHeight, setComputedRowHeight] = useState(MIN_ROW_HEIGHT);

  const layoutItems = ids.map((id, index) => {
    const entry = ctx.getComponentEntry(id);
    const gridPosition =
      (entry?.gridPosition as { x: number; y: number; width: number; height: number } | undefined) ?? {
        ...DEFAULT_GRID_POSITION,
        y: index * DEFAULT_GRID_POSITION.height,
      };
    return { i: id, x: gridPosition.x, y: gridPosition.y, w: gridPosition.width, h: gridPosition.height };
  });

  const rows = layoutItems.reduce((max, item) => Math.max(max, item.y + item.h), 0);

  useEffect(() => {
    const updateRowHeight = () => {
      const availableHeight = layoutContainerRef.current?.clientHeight || FALLBACK_AVAILABLE_HEIGHT;
      setComputedRowHeight(computeViewportFillRowHeight(availableHeight, rows));
    };

    updateRowHeight();

    // ResizeObserver, not a one-shot delayed measurement: the container reaches its final flexed
    // height asynchronously (chrome mounts, fonts, toolbar expansion), and a measurement taken too
    // early clamps to MIN_ROW_HEIGHT and would otherwise never correct itself — `window.resize`
    // alone cannot see container-only box changes. Legacy `MetaspaceGrid` retains its one-shot
    // flaw untouched while dual-read lives; this branch fixes it at the source.
    //
    // Viewport-fill REQUIRES a definite-height ancestor chain, because `.space-grid-adapter` is
    // `height: 100%`. Nested in a content-sized parent (e.g. a `ContextShell` docked section, which
    // declares no height) the observed box IS this grid's own height, so the equation is solved
    // against its own output. That is a stable fixed point rather than a runaway loop — the
    // recomputed rowHeight equals the one that produced the box, so it settles in one pass and the
    // grid stays at whatever it first measured (MIN_ROW_HEIGHT) — exactly the behaviour legacy
    // `MetaspaceGrid` already has in that position. Giving nesting hosts a definite height is the
    // real fix and is tracked separately; do not "fix" it here by observing an ancestor, which
    // reintroduces the feedback path without bounding it.
    const container = layoutContainerRef.current;
    if (typeof ResizeObserver !== 'undefined' && container) {
      const observer = new ResizeObserver(updateRowHeight);
      observer.observe(container);
      return () => observer.disconnect();
    }

    // jsdom / very old engines: fall back to the legacy window-resize behavior.
    window.addEventListener('resize', updateRowHeight);
    return () => window.removeEventListener('resize', updateRowHeight);
  }, [rows]);

  const rowHeight = typeof props.rowHeight === 'number' ? props.rowHeight : computedRowHeight;

  // The composer's Compose mode is the only editable case (EAINT-844 Phase F5) — `ctx.env`
  // carries both flags together, so a host that forgets to gate one still can't drag/resize
  // without also wiring the write-back callback.
  const gridEditable = Boolean(ctx.env.gridEditable) && typeof ctx.env.onGridPositionChange === 'function';
  const handleLayoutItemChange = useCallback(
    (item: Layout) => {
      ctx.env.onGridPositionChange?.(item.i, { x: item.x, y: item.y, width: item.w, height: item.h });
    },
    [ctx.env]
  );

  return (
    <div className="space-grid-adapter" data-component-id={componentId} data-surface-layout="uniform" ref={layoutContainerRef}>
      <ResponsiveGridLayout
        className="space-grid-adapter__layout"
        layouts={{ lg: layoutItems }}
        breakpoints={BREAKPOINTS}
        cols={{ ...DEFAULT_COLS, lg: columns }}
        rowHeight={rowHeight}
        margin={[10, 10]}
        isDraggable={gridEditable}
        isResizable={gridEditable}
        onDragStop={gridEditable ? (_layout, _oldItem, newItem) => handleLayoutItemChange(newItem) : undefined}
        onResizeStop={gridEditable ? (_layout, _oldItem, newItem) => handleLayoutItemChange(newItem) : undefined}
      >
        {ids.map((id) => {
          // Port of `MetaspaceGrid.tsx`'s "hide ArmillaryActions while the toolbar is open" —
          // the toolbar and the actions panel are mutually exclusive real estate (SC2).
          const entry = ctx.getComponentEntry(id);
          const isHiddenArmillaryActions = entry?.component === 'ArmillaryActions' && Boolean(ctx.env.toolbarOpen);
          return (
            <div
              key={id}
              className={[
                'space-grid-adapter__item',
                isHiddenArmillaryActions ? 'space-grid-adapter__item--hidden' : '',
                // Component-type class lets CSS target a specific item without
                // hardcoding it — e.g. .space-grid-adapter__item--preferences
                // can override the inline height that react-grid-layout injects.
                entry?.component
                  ? `space-grid-adapter__item--${String(entry.component).toLowerCase()}`
                  : '',
              ].filter(Boolean).join(' ')}
              data-component-type={entry?.component ?? undefined}
            >
              {ctx.renderChild(id)}
            </div>
          );
        })}
      </ResponsiveGridLayout>
    </div>
  );
};

export default SpaceGridAdapter;
